import React, { useMemo, useState } from 'react';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Expander from '@amzn/meridian/expander';
import IntervalSummaryHeatmap from './IntervalSummaryHeatmap';
import SegmentedControl from './SegmentedControl';
import { formatDuration } from './laborOpportunitiesModel';
import { getCycleDisplayName } from '../../utils/helpers';
import './shared/flowPlannerShared.css';

const VIEW_MODES = {
  hc: { label: 'Headcount', headerLabel: 'Headcount', totalLabel: 'Total HC Needed', decimals: 1 },
  laborHrs: { label: 'Labor Hours', headerLabel: 'Labor Hours', totalLabel: 'Total Labor Hrs', decimals: 2 }
};

const VIEW_MODE_OPTIONS = Object.entries(VIEW_MODES).map(([value, { label }]) => ({ value, label }));

const formatTo = (decimals) => (value) => Number(value ?? 0).toFixed(decimals);

const buildSlots = (intervals) => {
  if (!intervals?.length) return [];
  return intervals.map((iv, idx) => ({
    primary: iv.name || `P${idx + 1}`,
    secondary: `${iv.startLabel || ''}–${iv.endLabel || ''}`,
    tertiary: formatDuration(iv.endTime - iv.startTime),
    indicatorStatus: 'info'
  }));
};

const pickCycleSeries = (cycle, isLaborHrs) => (isLaborHrs ? cycle.laborHours : cycle.absoluteHC);
const pickGrandSeries = (grandTotals, isLaborHrs) => (isLaborHrs ? grandTotals.laborHours : grandTotals.absoluteHC);

const LaborSummary = ({ laborSummary, intervals }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [viewMode, setViewMode] = useState('hc');
  const isLaborHrs = viewMode === 'laborHrs';

  const { headerLabel, totalLabel, decimals } = VIEW_MODES[viewMode];
  const formatValue = useMemo(() => formatTo(decimals), [decimals]);

  const slots = useMemo(() => buildSlots(intervals), [intervals]);

  const rows = useMemo(() => {
    if (!laborSummary) return [];
    const { cycles, grandTotals } = laborSummary;
    const cycleRows = cycles.map((cycle) => ({
      label: getCycleDisplayName(cycle.name),
      dotKind: 'cycle',
      hideZero: false,
      values: pickCycleSeries(cycle, isLaborHrs)
    }));
    const totalRow = {
      label: totalLabel,
      dotKind: 'total',
      emphasize: true,
      hideZero: false,
      values: pickGrandSeries(grandTotals, isLaborHrs)
    };
    return [...cycleRows, totalRow];
  }, [laborSummary, isLaborHrs, totalLabel]);

  if (!laborSummary) return null;

  return (
    <div className="fp-theme">
      <Column spacing="300" alignmentHorizontal="stretch" width="100%">
        <Expander open={isExpanded} onChange={setIsExpanded} title="Labor Summary" type="inline" spacingInset="300">
          <Column spacing="100" alignmentHorizontal="stretch" width="100%">
            <Row spacing="200" alignmentHorizontal="end" alignmentVertical="center">
              <SegmentedControl
                value={viewMode}
                options={VIEW_MODE_OPTIONS}
                onChange={setViewMode}
                ariaLabel="View as headcount or labor hours"
              />
            </Row>
            <Box spacingInset="300 none none none">
              <IntervalSummaryHeatmap headerLabel={headerLabel} slots={slots} rows={rows} formatValue={formatValue} />
            </Box>
          </Column>
        </Expander>
      </Column>
    </div>
  );
};

export default LaborSummary;
